import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { Box, Button } from '@mui/material'
import { useAtom, useSetAtom } from 'jotai'
import { useSearchParams } from 'react-router-dom'
import { Item } from '../../../common/snippets'
import { ReportHeading } from '../common/ReportHeading'
import DropDownOA from '../../../common/dropDownOA'
import Datepicker from '../../../common/datePicker'
import * as sx from '../../../common/stylesInline/report_daily_branch'
import { alertAtom, globalLoaderAtom } from '../../../common/jotaiState/globalStateAtoms'
import { CargoRoleRightsAtom } from '../../../common/jotaiState/commonStateAtoms'
import { ChildCompanyListData, fetchChildCompanyListData } from '../../../common/jotaiState/reportStateAtom'
import { GetRequest } from '../../../common/api'
import { urls } from '../../../common/constants'
import useToken from '../../../helpers/useTokens'
import { isEmpty, removeDuplicatesFromArray } from '../../../common/helper'
import { RefreshSvg } from '../../../common/svg/refresh'
import { getDecryptedReportParams } from '../../../common/loginDecode'
import { downloadPDF } from '../analysisReport/pdfAutotable'
import {
  criteriaHeaders,
  reportName,
  reportViewOptions,
  shortReceiveReportHeaders,
  totalFields,
} from './constants'

const ShortReceiveReport = () => {
  const [urlSearchParams] = useSearchParams()
  const searchParams = getDecryptedReportParams(urlSearchParams)
  let { loginData = {} } = useToken()
  let isFromUrl = false
  let AllowViewAllBranchReportFromUrl = 0
  if (!loginData) {
    loginData = {}
    loginData.intCompanyID = Number(searchParams.get('C') || 0)
    loginData.intUserID = Number(searchParams.get('U') || 0)
    loginData.intBranchID = Number(searchParams.get('B') || 0)
    loginData.strCompanyName = searchParams.get('CN') || ''
    loginData.strUserName = searchParams.get('UN') || ''
    loginData.strBranchName = searchParams.get('BN') || ''
    loginData.IsBranchAdmin = Number(searchParams.get('IBA') || 0)
    loginData.IsMTPLAdmin = Number(searchParams.get('IMPTLA') || 0)
    loginData.IsAdmin = Number(searchParams.get('IA') || 0)
    AllowViewAllBranchReportFromUrl = Number(searchParams.get('AllowViewAllBranchReport') || 0)
    isFromUrl = true
  }

  const [cargoRoleRights] = useAtom(CargoRoleRightsAtom)
  const { AllowViewAllBranchReport = 0 } = cargoRoleRights?.Table1?.[0] || {}
  const allowViewAllFlag = isFromUrl ? AllowViewAllBranchReportFromUrl : AllowViewAllBranchReport
  const allowViewAllReport =
    allowViewAllFlag == 1 ||
    loginData?.IsBranchAdmin == 1 ||
    loginData?.IsMTPLAdmin == 1 ||
    loginData?.IsAdmin == 1
      ? 1
      : 0

  const setLoader = useSetAtom(globalLoaderAtom)
  const [, setAlert] = useAtom(alertAtom)
  const [, fetchChildCompanyList] = useAtom(fetchChildCompanyListData)
  const [ChildCompanyList] = useAtom(ChildCompanyListData)

  const [selCompany, setSelCompany] = useState(null)
  const [fromDate, setFromDate] = useState(moment(new Date()))
  const [toDate, setToDate] = useState(moment(new Date()))
  const [dispatchBranchList, setDispatchBranchList] = useState([])
  const [selDispatchBranch, setSelDispatchBranch] = useState('')
  const [userList, setUserList] = useState([])
  const [selUser, setSelUser] = useState('')
  const [shortReceiveBranchList, setShortReceiveBranchList] = useState([])
  const [selShortReceiveBranch, setSelShortReceiveBranch] = useState('')
  const [vehicleList, setVehicleList] = useState([])
  const [selVehicle, setSelVehicle] = useState('')
  const [paymentTypeList, setPaymentTypeList] = useState([])
  const [paymentType, setPaymentType] = useState('')
  const [reportView, setReportView] = useState(reportViewOptions?.[0])
  const [filtersLoaded, setFiltersLoaded] = useState(false)

  const filterItemSx = isFromUrl ? sx.itemsxGrow : sx.itemsx

  useEffect(() => {
    if (!isFromUrl && loginData?.intCompanyID) {
      fetchChildCompanyList({
        intCompanyID: loginData?.intCompanyID,
        UserID: loginData?.intUserID,
      })
    }
  }, [ loginData?.intCompanyID, isFromUrl])

  useEffect(() => {
    if (loginData?.intCompanyID) {
      if (isFromUrl) {
        setSelCompany({
          CompanyID: loginData?.intCompanyID,
          CompanyName: loginData?.strCompanyName,
        })
        return
      }
      const defaultCompany =
        ChildCompanyList.find((v) => v.CompanyID === loginData.intCompanyID) || {
          CompanyID: loginData?.intCompanyID,
          CompanyName: loginData?.strCompanyName,
        }
      setSelCompany(defaultCompany)
    }
  }, [ChildCompanyList,  loginData?.intCompanyID, isFromUrl])

  const getNumber = (value) => {
    const numberValue = Number(String(value ?? 0).replace(/,/g, ''))
    return Number.isFinite(numberValue) ? numberValue : 0
  }

  const buildOptions = (rows, idKey, nameKey, allOption) => {
    const mapped = rows
      .map((r) => ({ [idKey]: r[idKey], [nameKey]: r[nameKey] }))
      .filter((r) => r[idKey] != null && r[idKey] !== '' && r[nameKey])
    const unique = removeDuplicatesFromArray(mapped, idKey)
    if (unique.length > 1) return [allOption, ...unique]
    return unique
  }

  const formatData = (rows = []) => {
    const entryRows = rows.map((item, index) => ({
      ...item,
      srNo: index + 1,
      LRNO: item.LRNO || item.LRNo || '',
      PaymentType: item.PaymentTypeName || item.PaymentType || item.PayType || '',
      BookingDateTime: item.BookingDateTime || item.DispatchDateTime || item.DispatchDate || '',
      ShortReceiveDate: item.ShortReceiveDate || item.ReceiveDateTime || '',
      Sender: item.Sender || item.SenderName || '',
      RecName: item.RecName || item.Receiver || '',
      FromCity: item.FromCity || '',
      ToCity: item.ToCity || '',
      VehicleNo: item.VehicleNo || item.Vehicle || '',
      ShortQty: item.ShortQty ?? item.ShortUnit ?? 0,
      OtherCharge: item.OtherCharge ?? item.OtherCharges ?? 0,
      NetAmount: item.NetAmount ?? item.TotalAmount ?? 0,
      ReceivedByUser: item.ShortReceivedBy || item.ReceivedByUser || '',
      ShortReceivedByBranch: item.ShortReceivedByBranch || '',
      dataType: 'entry',
    }))

    const totals = totalFields.reduce((acc, field) => {
      acc[field] = entryRows.reduce((sum, row) => sum + getNumber(row[field]), 0)
      return acc
    }, {})

    return [
      ...entryRows,
      {
        srNo: 'Total',
        ...totals,
        dataType: 'total',
        align: { srNo: 'right' },
        colSpan: {
          srNo: shortReceiveReportHeaders.findIndex((h) => h.field === 'ShortQty'),
        },
      },
    ]
  }

  const openViewReport = (reportPayload) => {
    const rid = Date.now().toString()
    if (!window.generatedReports) {
      window.generatedReports = {}
    }
    window.generatedReports[rid] = reportPayload
    window.open(`/view-report?rid=${rid}`, '_blank')
  }

  const resetDependentFilters = () => {
    setDispatchBranchList([])
    setSelDispatchBranch('')
    setUserList([])
    setSelUser('')
    setShortReceiveBranchList([])
    setSelShortReceiveBranch('')
    setVehicleList([])
    setSelVehicle('')
    setPaymentTypeList([])
    setPaymentType('')
    setFiltersLoaded(false)
  }

  const criteriaData = [
    {
      selectedCompany: selCompany?.CompanyName || loginData?.strCompanyName || '',
      fromDate: moment(fromDate).format('DD-MMM-YYYY'),
      toDate: moment(toDate).format('DD-MMM-YYYY'),
      dispatchByBranch: selDispatchBranch?.DispatchByBranch || 'All',
      shortReceiveByUser: selUser?.ShortReceivedByUser || 'All',
      shortReceiveByBranch: selShortReceiveBranch?.ShortReceivedByBranch || 'All',
      vehicleNumber: selVehicle?.VehicleNo || 'All',
      paymentType: paymentType?.PaymentTypeName || 'All',
      reportView: reportView?.view || 'Detailed',
      dataType: 'entry',
    },
  ]

  const handleClickGo = () => {
    if (Math.abs(toDate.diff(fromDate, 'days')) > 31) {
      setAlert({ show: true, msg: 'This report cannot be taken for more than 31 days', nextId: '' })
      return
    }

    setLoader(true)
    const data = {
      BranchID: loginData?.intBranchID || 0,
      CompanyID: loginData?.intCompanyID || 0,
      DateFrom: moment(fromDate).format('YYYY-MM-DD'),
      DateTo: moment(toDate).format('YYYY-MM-DD'),
      UserID: 0,
      DispatchByBranch: 0,
      ShortReceivedByUserID: 0,
      ShortReceivedByBranch: 0,
      VehicleID: 0,
      PaymentType: 0,
      AllowViewAllReport: allowViewAllReport,
    }

    GetRequest(urls.getShortReceiveReportDetails, data)
      .then((res) => {
        if (res.status !== 'Success') {
          throw new Error(res?.ErrMessage || 'Request Failed')
        }

        const rows = Array.isArray(res?.Data?.Table) ? res.Data.Table : []
        if (!rows.length) {
          throw new Error('No Data Found')
        }

        const nextDispatchList = buildOptions(rows, 'DispatchByBranchID', 'DispatchByBranch', {
          DispatchByBranchID: 0,
          DispatchByBranch: 'All',
        })
        const userRows = rows.map((r) => ({
          ShortReceivedByUserID: r.ShortReceivedByUserID,
          ShortReceivedByUser: r.ShortReceivedBy || '',
        }))
        const nextUserList = buildOptions(userRows, 'ShortReceivedByUserID', 'ShortReceivedByUser', {
          ShortReceivedByUserID: 0,
          ShortReceivedByUser: 'All',
        })
        const nextShortBranchList = buildOptions(
          rows,
          'ShortReceivedByBranchID',
          'ShortReceivedByBranch',
          {
            ShortReceivedByBranchID: 0,
            ShortReceivedByBranch: 'All',
          }
        )
        const nextVehicleList = buildOptions(rows, 'VehicleID', 'VehicleNo', {
          VehicleID: 0,
          VehicleNo: 'All',
        })
        const nextPaymentList = buildOptions(rows, 'PaymentTypeID', 'PaymentTypeName', {
          PaymentTypeID: 0,
          PaymentTypeName: 'All',
        })

        setDispatchBranchList(nextDispatchList)
        setUserList(nextUserList)
        setShortReceiveBranchList(nextShortBranchList)
        setVehicleList(nextVehicleList)
        setPaymentTypeList(nextPaymentList)
        setSelDispatchBranch(nextDispatchList[0] || '')
        setSelUser(nextUserList[0] || '')
        setSelShortReceiveBranch(nextShortBranchList[0] || '')
        setSelVehicle(nextVehicleList[0] || '')
        setPaymentType(nextPaymentList[0] || '')
        setFiltersLoaded(true)
      })
      .catch((err) => {
        resetDependentFilters()
        setAlert({ show: true, msg: err.message || 'Request Failed', nextId: '' })
      })
      .finally(() => setLoader(false))
  }

  const handleGenerate = (isPdf = false) => {
    if (!selCompany?.CompanyID) {
      setAlert({ show: true, msg: 'Please select a company', nextId: '' })
      return
    }
    if (!filtersLoaded) {
      setAlert({ show: true, msg: 'Please click GO to load filters first', nextId: '' })
      return
    }
    if (Math.abs(toDate.diff(fromDate, 'days')) > 31) {
      setAlert({ show: true, msg: 'This report cannot be taken for more than 31 days', nextId: '' })
      return
    }
    if (selDispatchBranch === '' || selDispatchBranch === ' ') {
      setAlert({ show: true, msg: 'Please select Dispatch By Branch', nextId: '' })
      return
    }
    if (selUser === '' || selUser === ' ') {
      setAlert({ show: true, msg: 'Please select Short receive By user', nextId: '' })
      return
    }
    if (selShortReceiveBranch === '' || selShortReceiveBranch === ' ') {
      setAlert({ show: true, msg: 'Please select Short receive By Branch', nextId: '' })
      return
    }
    if (selVehicle === '' || selVehicle === ' ') {
      setAlert({ show: true, msg: 'Please select Vehicle Number', nextId: '' })
      return
    }
    if (paymentType === '' || paymentType === ' ') {
      setAlert({ show: true, msg: 'Please select Payment Type', nextId: '' })
      return
    }
    if (reportView === '' || reportView === ' ') {
      setAlert({ show: true, msg: 'Please select Report View', nextId: '' })
      return
    }

    setLoader(true)
    const data = {
      BranchID: loginData?.intBranchID || 0,
      CompanyID: selCompany.CompanyID,
      DateFrom: moment(fromDate).format('YYYY-MM-DD'),
      DateTo: moment(toDate).format('YYYY-MM-DD'),
      UserID: loginData?.intUserID || 0,
      DispatchByBranch: selDispatchBranch?.DispatchByBranchID || 0,
      ShortReceivedByUserID: selUser?.ShortReceivedByUserID || 0,
      ShortReceivedByBranch: selShortReceiveBranch?.ShortReceivedByBranchID || 0,
      VehicleID: selVehicle?.VehicleID || 0,
      PaymentType: paymentType?.PaymentTypeID ?? 0,
      AllowViewAllReport: allowViewAllReport,
    }

    GetRequest(urls.getShortReceiveReportDetails, data)
      .then((res) => {
        if (res.status !== 'Success') {
          throw new Error(res?.ErrMessage || 'Request Failed')
        }
        const rows = Array.isArray(res?.Data?.Table) ? res.Data.Table : []
        if (!rows.length) {
          throw new Error('No Data Found')
        }
        const formattedData = formatData(rows)
        if (isPdf) {
          downloadPDF({
            reportName,
            criteriaHeaders,
            criteriaData,
            tableData: formattedData,
            visibleColumns: shortReceiveReportHeaders,
            loginData,
            hideHeader: false,
          })
          return
        }
        openViewReport({
          reportName,
          criteriaHeaders,
          criteriaData,
          tableData: formattedData,
          mainHeader: shortReceiveReportHeaders,
          showColumnBorders: true,
        })
      })
      .catch((err) => {
        setAlert({ show: true, msg: err.message || 'Request Failed', nextId: '' })
      })
      .finally(() => setLoader(false))
  }

  const reset = () => {
    setFromDate(moment(new Date()))
    setToDate(moment(new Date()))
    setReportView(reportViewOptions?.[0])
    resetDependentFilters()
    if (loginData?.intCompanyID) {
      const defaultCompany =
        ChildCompanyList.find((v) => v.CompanyID === loginData.intCompanyID) || {
          CompanyID: loginData.intCompanyID,
          CompanyName: loginData.strCompanyName,
        }
      setSelCompany(defaultCompany)
    }
  }

  return (
    <Item sx={{ padding: '0' }} className="reports">
      {isFromUrl && <style>{`.global-header { display: none; } #root { zoom: 1.2; }`}</style>}
      {!isFromUrl && <ReportHeading reportName={reportName} />}
      <Box
        sx={{ ...sx.reportFlex, gap: isFromUrl ? '15px' : '10px' }}
        style={{ background: '#fff', borderBottom: '1px solid #ccc', paddingBottom: '10px' }}
      >
        {!isFromUrl && (
          <Item sx={sx.itemsxGrow}>
            <DropDownOA
              id="selectCompany"
              label="Selected Company"
              data={ChildCompanyList}
              defaultvalue={selCompany?.CompanyName || ''}
              disabled={true}
              onSelect={setSelCompany}
              dataKey="CompanyName"
              idKey="CompanyID"
              width="100%"
            />
          </Item>
        )}
        <Item
          sx={isFromUrl ? sx.itemsxGrow : sx.itemsxGrow}
          style={{ width: isFromUrl ? 'auto' : '140px' }}
          className="input--container bookingDatePicker"
        >
          <Datepicker
            name="from_short_receive_date"
            value={fromDate}
            label="From Short receive Date"
            id="shortReceiveFromDate"
            dateChange={(date) => {
              setFromDate(date)
              resetDependentFilters()
            }}
            width="100%"
          />
        </Item>
        <Item
          sx={isFromUrl ? { ...sx.itemsxGrow, width: '60%' } : sx.itemsxGrow}
          style={{ width: isFromUrl ? undefined : '140px' }}
          className="input--container bookingDatePicker"
        >
          <Datepicker
            name="to_short_receive_date"
            value={toDate}
            label="To Short receive date"
            id="shortReceiveToDate"
            dateChange={(date) => {
              setToDate(date)
              resetDependentFilters()
            }}
            width="100%"
          />
        </Item>
        <Item id="shortReceiveGo" tabIndex={0} sx={filterItemSx}>
          <Button
            id="shortReceiveGoBtn"
            variant="contained"
            className="btn btn-primary"
            onClick={handleClickGo}
            sx={{ width: '100%', letterSpacing: '2px', height: '40px' }}
          >
            GO
          </Button>
        </Item>
        <Item sx={filterItemSx} style={{ width: isFromUrl ? 'auto' : '180px' }}>
          <DropDownOA
            id="dispatchByBranch"
            label="Dispatch By Branch"
            defaultvalue={selDispatchBranch?.DispatchByBranch || ''}
            data={dispatchBranchList}
            dataKey="DispatchByBranch"
            idKey="DispatchByBranchID"
            onSelect={setSelDispatchBranch}
            width="100%"
            listWidth="300px"
          />
        </Item>
        <Item sx={filterItemSx} style={{ width: isFromUrl ? 'auto' : '180px' }}>
          <DropDownOA
            id="shortReceiveByUser"
            label="Short receive By user"
            defaultvalue={selUser?.ShortReceivedByUser || ''}
            data={userList}
            dataKey="ShortReceivedByUser"
            idKey="ShortReceivedByUserID"
            onSelect={setSelUser}
            width="100%"
            listWidth="300px"
          />
        </Item>
        <Item sx={filterItemSx} style={{ width: isFromUrl ? 'auto' : '180px' }}>
          <DropDownOA
            id="shortReceiveByBranch"
            label="Short receive By Branch"
            defaultvalue={selShortReceiveBranch?.ShortReceivedByBranch || ''}
            data={shortReceiveBranchList}
            dataKey="ShortReceivedByBranch"
            idKey="ShortReceivedByBranchID"
            onSelect={setSelShortReceiveBranch}
            width="100%"
            listWidth="300px"
          />
        </Item>
        <Item sx={filterItemSx} style={{ width: isFromUrl ? 'auto' : '180px' }}>
          <DropDownOA
            id="vehicleNumber"
            label="Vehicle Number"
            defaultvalue={selVehicle?.VehicleNo || ''}
            data={vehicleList}
            dataKey="VehicleNo"
            idKey="VehicleID"
            onSelect={setSelVehicle}
            width="100%"
            listWidth="250px"
          />
        </Item>
      </Box>
      <Box
        sx={{ ...sx.reportFlex, gap: isFromUrl ? '15px' : '10px' }}
        style={{ background: '#fff', paddingTop: '10px', paddingBottom: '10px' }}
      >
        <Item sx={filterItemSx} style={{ width: isFromUrl ? 'auto' : '160px' }}>
          <DropDownOA
            id="paymentType"
            label="Payment Type"
            defaultvalue={paymentType?.PaymentTypeName || ''}
            data={paymentTypeList}
            dataKey="PaymentTypeName"
            idKey="PaymentTypeID"
            onSelect={setPaymentType}
            width="100%"
          />
        </Item>
        <Item sx={filterItemSx} style={{ width: isFromUrl ? 'auto' : '160px' }}>
          <DropDownOA
            id="reportView"
            label="Report View"
            defaultvalue={reportView?.view || ''}
            data={reportViewOptions}
            dataKey="view"
            idKey="id"
            onSelect={setReportView}
            width="100%"
          />
        </Item>
        <Item id="generateDiv" tabIndex={0} sx={filterItemSx}>
          <Button
            id="shortReceiveGenerateBtn"
            variant="contained"
            className="btn btn-primary"
            onClick={() => handleGenerate(isFromUrl)}
            sx={{ width: '100%', letterSpacing: '1px', height: '40px', whiteSpace: 'nowrap' }}
          >
            {isFromUrl ? 'DOWNLOAD PDF' : 'GENERATE NEW FORMAT'}
          </Button>
        </Item>
        {!isFromUrl && (
          <Item sx={{ ...sx.itemsx }} style={{ cursor: 'pointer' }} onClick={reset}>
            <RefreshSvg width="24px" height="24px" />
          </Item>
        )}
      </Box>
    </Item>
  )
}

export default ShortReceiveReport
